// ─── Le richieste del calendario, in un posto solo ──────────────────────────
//
// 🔒 Lo stato del server sta in TanStack Query e in nessun altro posto
// (§ 03.2): niente store globale con una seconda copia delle prenotazioni.

import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type {
  AmbitoSerie,
  Calendario,
  CreaManutenzione,
  CreaPrenotazione,
  CreaSerieChiesta,
  ModificaPrenotazioneChiesta,
  ModificaSerie,
  PostoInCampo,
  SchedaPrenotazione,
  SerieAggiornata,
  SerieCreata,
  TariffeLezioni,
  VolteAnnullate,
  VolteDellaSerie,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { ETICHETTE_AMBITO } from '@pmo/shared';

export function usaCalendario(data: string, attaccato: boolean) {
  return useQuery({
    queryKey: ['calendario', data],
    queryFn: () => chiama<Calendario>('GET', `/calendar?date=${data}`),
    /**
     * ⚖️ Quando il canale in tempo reale è attaccato, la rilettura periodica è
     * una rete di sicurezza rara; quando è caduto, diventa **l'unico** modo di
     * accorgersi di cosa fa l'altro operatore.
     * 📌 Il tempo reale è una comodità, non la fonte della verità: il prezzo di
     *    un socket caduto è leggere più spesso, non vedere il passato.
     */
    refetchInterval: attaccato ? 120_000 : 20_000,
    refetchOnWindowFocus: true,
  });
}

export function usaScheda(id: string | null) {
  return useQuery({
    queryKey: ['prenotazione', id],
    queryFn: () => chiama<SchedaPrenotazione>('GET', `/bookings/${id}`),
    enabled: id !== null,
  });
}

/** Rilegge il giorno e la scheda: dopo una scrittura niente resta com'era. */
function usaRiletture() {
  const qc = useQueryClient();
  return (data: string, id?: string) => {
    void qc.invalidateQueries({ queryKey: ['calendario', data] });
    if (id) void qc.invalidateQueries({ queryKey: ['prenotazione', id] });
  };
}

export function usaCreazione(data: string): UseMutationResult<SchedaPrenotazione, Error, CreaPrenotazione> {
  const rileggi = usaRiletture();
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'prenotazione creata', domande: ['ORA_RISERVATA'] },
    mutationFn: (b: CreaPrenotazione) => chiama<SchedaPrenotazione>('POST', '/bookings', b),
    onSuccess: (p) => {
      /**
       * ⭐⭐ **LA RISPOSTA DELLA CREAZIONE È GIÀ LA SCHEDA, e va messa in cassetto
       * PRIMA che il pannello la chieda.**
       *
       * Il pannello passa alla scheda nell'istante stesso in cui questa riga
       * finisce. Senza, `usaScheda` partirebbe da vuoto e per un giro di
       * risposta si vedrebbe *«sto leggendo…»* — cioè, all'occhio, **una
       * seconda scheda che si apre**: esattamente la cosa che lui ha chiesto di
       * togliere. La rotta rende una `SchedaPrenotazione` intera, quindi non
       * c'è niente da indovinare: è la stessa che arriverebbe rileggendo.
       * 📌 *Un'attesa che dura un battito non si nota perché è breve: si nota
       *    perché è un'altra schermata.*
       */
      qc.setQueryData(['prenotazione', p.id], p);
      rileggi(data);
      if (p.date !== data) rileggi(p.date);
    },
  });
}

/**
 * ⭐⭐ **LA MANUTENZIONE SU PIÙ CAMPI** — 17/09/2026, richiesta sua.
 *
 * ⚖️ Rende un ELENCO perché il gesto è uno e le prenotazioni sono tante: il
 * pannello apre la scheda solo quando il campo era uno, perché con quattro non
 * esiste «la» scheda da aprire — e aprirne una a caso sarebbe peggio che non
 * aprirne nessuna.
 *
 * 🔒 Le mette tutte in cassetto prima di rileggere, come la creazione normale:
 * la scheda che si apre subito dopo deve trovarcela già.
 */
export function usaCreazioneManutenzione(
  data: string,
): UseMutationResult<SchedaPrenotazione[], Error, CreaManutenzione> {
  const rileggi = usaRiletture();
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (d) =>
        (d as unknown[]).length === 1
          ? 'manutenzione segnata'
          : `manutenzione segnata su ${(d as unknown[]).length} campi`,
    },
    mutationFn: (b: CreaManutenzione) =>
      chiama<{ items: SchedaPrenotazione[] }>('POST', '/bookings/manutenzione', b).then((r) => r.items),
    onSuccess: (items) => {
      for (const p of items) qc.setQueryData(['prenotazione', p.id], p);
      rileggi(data);
      const altroGiorno = items.find((p) => p.date !== data);
      if (altroGiorno) rileggi(altroGiorno.date);
    },
  });
}

/**
 * 🔁⭐⭐ **UNA PRENOTAZIONE CHE SI RIPETE** — 17/09/2026, richiesta sua.
 *
 * ⚖️ Rende una cosa DIVERSA dalla creazione normale — la prima nata, quante ne
 * sono nate e **i giorni saltati** — ed è per questo che è un gancio suo e non
 * un parametro in più: chi la chiama deve guardare i giorni saltati, e un
 * risultato che a volte li ha e a volte no è un risultato che nessuno guarda.
 *
 * 🚨 **Si rilegge TUTTO il calendario, non il solo giorno di partenza**: le
 * altre nascono in giorni che nessuna chiave in cassetto conosce, e un
 * `invalidate` mirato le lascerebbe invisibili finché qualcuno non naviga.
 */
export function usaCreazioneSerie(): UseMutationResult<SerieCreata, Error, CreaSerieChiesta> {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (d) => `periodicità creata: ${(d as SerieCreata).quante} prenotazioni`,
    },
    mutationFn: (b: CreaSerieChiesta) => chiama<SerieCreata>('POST', '/bookings/serie', b),
    onSuccess: (r) => {
      qc.setQueryData(['prenotazione', r.prima.id], r.prima);
      void qc.invalidateQueries({ queryKey: ['calendario'] });
    },
  });
}

/**
 * 🔁 **Spostare la FINE di una periodicità**, nei due versi: più in là fa
 * nascere le volte che mancano, più in qua fa sparire quelle che avanzano.
 * ⇒ Si rilegge tutto, come per la creazione: tocca giorni che questa schermata
 *   non ha in cassetto.
 */
export function usaModificaSerie(serieId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'periodicità aggiornata' },
    mutationFn: (m: ModificaSerie) => chiama<SerieAggiornata>('PATCH', `/bookings/serie/${serieId}`, m),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendario'] });
      void qc.invalidateQueries({ queryKey: ['prenotazione'] });
      void qc.invalidateQueries({ queryKey: ['serie', serieId] });
    },
  });
}

/**
 * 🔁 Le date di una serie, per la sezione «Tutta la serie» (28/09/2026, voce 5). Si chiede solo
 * quando la sezione è aperta: la scheda di tutti i giorni non ne ha bisogno.
 */
export function usaVolteDellaSerie(serieId: string | null, aperta: boolean) {
  return useQuery({
    queryKey: ['serie', serieId],
    queryFn: () => chiama<VolteDellaSerie>('GET', `/bookings/serie/${serieId}`),
    enabled: serieId !== null && aperta,
  });
}

/**
 * 🔁🗑️ Annullare le date scelte di una serie (28/09/2026, voce 5). Fra le date può esserci quella
 * che si sta guardando: allora la sua scheda si butta, come con l'annullo di sempre.
 */
export function usaAnnullaVolte(serieId: string | null, idAperta: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (d: unknown) => {
        const n = (d as VolteAnnullate).tolte;
        return n === 1 ? 'una data annullata' : `${n} date annullate`;
      },
    },
    mutationFn: (a: { ids: string[] }) =>
      chiama<VolteAnnullate>('POST', `/bookings/serie/${serieId}/cancel`, a),
    onSuccess: (_, a) => {
      if (a.ids.includes(idAperta)) qc.removeQueries({ queryKey: ['prenotazione', idAperta] });
      void qc.invalidateQueries({ queryKey: ['calendario'] });
      void qc.invalidateQueries({ queryKey: ['prenotazione'] });
      void qc.invalidateQueries({ queryKey: ['serie', serieId] });
    },
  });
}

export interface Spostamento {
  id: string;
  /** Il giorno da cui parte: va riletto anche quello, se cambia. */
  daData: string;
  modifica: ModificaPrenotazioneChiesta;
}

/**
 * ⭐⭐ LO SPOSTAMENTO È OTTIMISTA, E TORNA INDIETRO SE IL SERVER DICE DI NO.
 *
 * Il blocco si muove subito — trascinare qualcosa che resta fermo mezzo secondo
 * sembra rotto — e se il server rifiuta (slot appena occupato) **torna dov'era**
 * e compare il motivo.
 *
 * ⚠️ `onMutate` deve prima FERMARE le riletture in volo: una risposta partita
 * prima della modifica, che arriva dopo, riscriverebbe la cache col vecchio e
 * il blocco tornerebbe indietro da solo senza che nessuno abbia rifiutato
 * niente.
 */
export function usaSpostamento(data: string) {
  const qc = useQueryClient();
  return useMutation({
    // 🚦 Fase B: la partita spostata a meno delle ore della disdetta chiede chi lo chiede.
    meta: { fatto: 'prenotazione spostata', domande: ['CHI_LO_CHIEDE', 'ORA_RISERVATA'] },
    mutationFn: (s: Spostamento) => chiama<SchedaPrenotazione>('PATCH', `/bookings/${s.id}`, s.modifica),
    onMutate: async (s: Spostamento) => {
      await qc.cancelQueries({ queryKey: ['calendario', data] });
      const prima = qc.getQueryData<Calendario>(['calendario', data]);
      qc.setQueryData<Calendario>(['calendario', data], (c) =>
        c
          ? {
              ...c,
              bookings: c.bookings.map((b) =>
                b.id === s.id
                  ? {
                      ...b,
                      courtId: s.modifica.courtId ?? b.courtId,
                      date: s.modifica.date ?? b.date,
                      time: s.modifica.time ?? b.time,
                      durationMinutes: s.modifica.durationMinutes ?? b.durationMinutes,
                    }
                  : b,
              ),
            }
          : c,
      );
      return { prima };
    },
    onError: (_err, _s, ctx) => {
      if (ctx?.prima) qc.setQueryData(['calendario', data], ctx.prima);
    },
    onSettled: (_p, _e, s) => {
      void qc.invalidateQueries({ queryKey: ['calendario', data] });
      if (s.daData !== data) void qc.invalidateQueries({ queryKey: ['calendario', s.daData] });
      void qc.invalidateQueries({ queryKey: ['prenotazione', s.id] });
    },
  });
}

export function usaModificaPrenotazione(data: string, id: string) {
  const rileggi = usaRiletture();
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'prenotazione aggiornata', domande: ['CHI_LO_CHIEDE', 'ORA_RISERVATA'] },
    mutationFn: (m: ModificaPrenotazioneChiesta) => chiama<SchedaPrenotazione>('PATCH', `/bookings/${id}`, m),
    onSuccess: (_, m) => {
      rileggi(data, id);
      // 🔁 Come per l'annullo: una modifica di serie tocca giorni che nessun
      //    cassetto di questa schermata conosce.
      if (m.ambito !== undefined && m.ambito !== 'una') {
        void qc.invalidateQueries({ queryKey: ['calendario'] });
      }
    },
  });
}

export function usaAnnullo(data: string, id: string) {
  const rileggi = usaRiletture();
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (_d, v) => {
        const a = (v as { ambito?: AmbitoSerie }).ambito;
        return a && a !== 'una'
          ? `prenotazioni annullate (${ETICHETTE_AMBITO[a]})`
          : 'prenotazione annullata';
      },
      // 🚦 Fase B: una partita a meno delle ore della disdetta chiede chi lo chiede.
      // 🔄 30/09/2026: la lezione della sera (la trattenuta al maestro) non lo chiede più.
      domande: ['CHI_LO_CHIEDE'],
    },
    mutationFn: (a: { reason: string | null; ambito?: AmbitoSerie; perChi?: 'socio' | 'circolo' }) =>
      chiama<SchedaPrenotazione>('POST', `/bookings/${id}/cancel`, a),
    onSuccess: (_, a) => {
      /**
       * ⚠️ **SI BUTTA, non si rilegge** — e dal 17/09/2026 è obbligatorio.
       *
       * Una prenotazione su cui non è passato niente adesso viene **cancellata
       * per davvero** dal server (⇒ `routes.ts`): invalidarla vorrebbe dire
       * chiederla di nuovo e prendersi un `404`, cioè far comparire un guasto
       * subito dopo un'operazione riuscita.
       * 📌 *Invalidare vuol dire «è cambiata, richiedila». Per una riga che non
       *    esiste più la parola giusta è un'altra.*
       */
      qc.removeQueries({ queryKey: ['prenotazione', id] });
      rileggi(data);
      /*
       * 🔁 Annullando una serie spariscono anche giorni che non si stanno
       * guardando: il cassetto di QUEL giorno non lo sa, e chi ci navigasse
       * troverebbe blocchi che non ci sono più.
       */
      if (a.ambito !== 'una') void qc.invalidateQueries({ queryKey: ['calendario'] });
    },
  });
}

export function usaAggiuntaGiocatore(data: string, id: string) {
  const rileggi = usaRiletture();
  return useMutation({
    meta: { fatto: 'giocatore aggiunto' },
    mutationFn: (g: PostoInCampo) => chiama<SchedaPrenotazione>('POST', `/bookings/${id}/players`, g),
    onSuccess: () => rileggi(data, id),
  });
}

export function usaModificaGiocatore(data: string, id: string) {
  const rileggi = usaRiletture();
  return useMutation({
    meta: { fatto: 'quota aggiornata' },
    mutationFn: (v: { playerId: string; amountDueCents: number }) =>
      chiama<SchedaPrenotazione>('PATCH', `/bookings/${id}/players/${v.playerId}`, {
        amountDueCents: v.amountDueCents,
      }),
    onSuccess: () => rileggi(data, id),
  });
}

export function usaUscitaGiocatore(data: string, id: string) {
  const rileggi = usaRiletture();
  return useMutation({
    meta: { fatto: 'giocatore tolto dalla prenotazione' },
    mutationFn: (playerId: string) => chiama<{ ok: true }>('DELETE', `/bookings/${id}/players/${playerId}`),
    onSuccess: () => rileggi(data, id),
  });
}

/**
 * 🎓 **Il «Listino prezzi lezioni», in lettura** — voce 19c. Serve al pannello
 * della prenotazione nuova per dire l'importo a testa PRIMA di «Crea». Si legge
 * dalla rotta del calendario, perché chi prenota non ha per forza il permesso
 * delle impostazioni.
 */
export function usaTariffeLezioni() {
  return useQuery({
    queryKey: ['tariffe-lezioni', 'calendario'],
    queryFn: () => chiama<TariffeLezioni>('GET', '/calendar/lesson-rates'),
    staleTime: 60_000,
  });
}
